import { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import BaseWrapper from "../../../components/ui/BaseWrapper";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import BaseLoader from "../../../components/ui/BaseLoader";
import BaseDropdownV2 from "../../../components/ui/BaseDropdownV2";
import { Avatar, Chip } from "@mui/material";
import { showToast } from "../../../store/toast/toastSlice";
import useAuth from "../../../hooks/useAuth";
import { fetchAllClients, deleteClient, deleteCustomerUser } from "../../../services/api/client";
import { activateCompanyUser, deactivateCompanyUser } from "../../../services/api/company";
import GenerateSignupModal from "../CreateEditClient/components/GenerateSignupModal";

import { debounce } from "../../../utils/helpers";
import { setClientDashboardFilters } from "../../../store/filters/clientDashboardFilterSlice";
import ClientDashboardFilters from "../components/ClientDashboardFilter";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import BaseButton from "../../../components/ui/BaseButton";
import { ROLES } from "../../../utils/variables";
import CompleteProfileModal from "../../WorkJobDetails/CompleteProfileModal";
import { fetchTeams } from "../../../services/api/teams";
import BaseTextField from "../../../components/ui/BaseTextField";
import { renderUserCell } from "../../../components/ui/UserProfileLink";

const ClientListingTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { auth, getPermissionForModule, authRole } = useAuth();
  const companyId = auth?.user?.company?.id;
  const { hasUpdateAccess, hasDeleteAccess, hasWriteAccess, hasReadAccess } =
    getPermissionForModule("CLIENT_CUSTOMER");
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
  const [isGenerateSignupOpen, setIsGenerateSignupOpen] = useState(false);
  const [isDeleteOwnerModalOpen, setIsDeleteOwnerModalOpen] = useState(false);
  const [isDeletingOwner, setIsDeletingOwner] = useState(false);
  const [isConfirmActivateOpen, setIsConfirmActivateOpen] = useState(false);
  const [isConfirmDeactivateOpen, setIsConfirmDeactivateOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const naviagte = useNavigate();
  const { userData } = useCurrentUser();
  const [isProfileCompleted, setIsProfileCompleted] = useState(false);

  const clientDashboardFilters = useSelector((store) => store.clientDashboardFilters.workOrders);
  const [searchInput, setSearchInput] = useState(clientDashboardFilters.search);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [menuOptions, setMenuOptions] = useState([]);

  const queryParams = useMemo(
    () => ({
      page: clientDashboardFilters.pagination.page + 1,
      limit: clientDashboardFilters.pagination.pageSize,
      search: clientDashboardFilters.search || "",
      clientId: clientDashboardFilters?.clientId?.value || "",
      teamIds: clientDashboardFilters.teamIds || undefined,
      country: clientDashboardFilters.country || "",
      branchId: clientDashboardFilters.branchId.value || "",
      taxType: clientDashboardFilters.taxType || "",
      manager: clientDashboardFilters.manager || "",
      projectsCount: clientDashboardFilters.projectsCount || "",
      totalWorkOrdersCount: clientDashboardFilters.totalWorkOrdersCount || "",
      publishedCount: clientDashboardFilters.publishedCount || "",
      assignedCount: clientDashboardFilters.assignedCount || "",
      workDoneCount: clientDashboardFilters.workDoneCount || "",
      approvedCount: clientDashboardFilters.approvedCount || "",
      paidCount: clientDashboardFilters.paidCount || "",
      isParentClient: "true",
    }),
    [clientDashboardFilters],
  );

  const {
    isLoading: isFetchingClients,
    data: clientsData,
    refetch: refetchClients,
  } = useQuery({
    queryKey: ["clients", queryParams],
    queryFn: () => fetchAllClients(queryParams),
    placeholderData: keepPreviousData,
  });
  const { isPending: isTeamsLoading, data: teamsData } = useQuery({
    queryKey: ["teams", companyId],
    queryFn: () =>
      fetchTeams({
        companyId: companyId?.toString(),
      }),
  });

  const [clientsList, setClientsList] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    if (clientsData?.data?.data) {
      setClientsList(clientsData.data.data);
      setAllClients(clientsData.data.data);
      setRowCount(clientsData.data.totalCount);
    }
  }, [clientsData]);

  useEffect(() => {
    const options = [];
    if (hasUpdateAccess)
      options.push({
        label: "Edit",
        value: "EDIT",
      });
    if (hasUpdateAccess)
      options.push({
        label: "Activate",
        value: "APPROVED",
      });
    if (hasDeleteAccess)
      options.push({
        label: "Delete",
        value: "DELETE",
        disabled: true,
      });
    if (hasDeleteAccess)
      options.push({
        label: "Deactivate",
        value: "DEACTIVATED",
      });
    setMenuOptions(options);
  }, [hasUpdateAccess, hasDeleteAccess]);

  const debouncedSearchRef = useRef(
    debounce((value) => {
      dispatch(
        setClientDashboardFilters({
          module: "workOrders",
          filterName: "search",
          data: value,
        }),
      );
    }, 500),
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearchRef.current(value);
  };

  const handleRemoveFilter = (key, id) => {
    if (key === "teamIds") {
      dispatch(
        setClientDashboardFilters({
          module: "workOrders",
          filterName: "teamIds",
          data: clientDashboardFilters.teamIds.filter((teamId) => teamId !== id),
        }),
      );
    } else {
      dispatch(
        setClientDashboardFilters({
          module: "workOrders",
          filterName: key,
          data: "",
        }),
      );
      dispatch(
        setClientDashboardFilters({
          module: "workOrders",
          filterName: "pagination",
          data: { page: 0, pageSize: clientDashboardFilters.pagination.pageSize },
        }),
      );
    }
    refetchClients();
  };
  const appliedFilters = useMemo(() => {
    const filters = [];
    const wf = clientDashboardFilters;
    // The search term is already visible in the search box, so it gets no chip.
    if (wf.manager) filters.push({ prefix: "Contact Person", value: wf.manager, key: "manager" });
    if (wf.branchId.value) {
      filters.push({
        prefix: "Tech Domain Branch",
        value: wf.branchId.label,
        key: "branchId",
      });
    }
    if (wf?.clientId?.value) {
      filters.push({
        prefix: "Customer",
        value: wf.clientId.label,
        key: "clientId",
      });
    }
    if (wf.country) filters.push({ prefix: "Country", value: wf.country, key: "country" });
    if (wf.taxType) filters.push({ prefix: "Tax Type", value: wf.taxType, key: "taxType" });
    if (wf.projectsCount)
      filters.push({
        prefix: "Projects",
        value: wf.projectsCount,
        key: "projectsCount",
      });
    if (wf.totalWorkOrdersCount)
      filters.push({
        prefix: "Total Work Orders",
        value: wf.totalWorkOrdersCount,
        key: "totalWorkOrdersCount",
      });
    if (wf.publishedCount)
      filters.push({
        prefix: "Published",
        value: wf.publishedCount,
        key: "publishedCount",
      });
    if (wf.assignedCount)
      filters.push({
        prefix: "Assigned",
        value: wf.assignedCount,
        key: "assignedCount",
      });
    if (wf.workDoneCount)
      filters.push({
        prefix: "Work Done",
        value: wf.workDoneCount,
        key: "workDoneCount",
      });
    if (wf.approvedCount)
      filters.push({
        prefix: "Approved",
        value: wf.approvedCount,
        key: "approvedCount",
      });
    if (wf.paidCount) filters.push({ prefix: "Paid", value: wf.paidCount, key: "paidCount" });

    if (wf?.teamIds?.length > 0) {
      const items = teamsData?.data?.data
        .filter((s) => clientDashboardFilters?.teamIds?.includes(s.id))
        .map((s) => ({ id: s.id, name: s.name }));

      items.forEach(({ id, name }) =>
        filters.push({
          prefix: "Team",
          value: name,
          key: "teamIds",
          id,
        }),
      );
    }
    return filters;
  }, [clientDashboardFilters]);

  const columns = [
    {
      pinLeft: true,
      field: "id",
      headerName: "ID",
      width: 100,
      renderCell: (params) => (
        <p
          className={`table-font-size font-normal cursor-pointer ${
            hasReadAccess || hasUpdateAccess ? "text-primary" : "text-gray-500"
          }`}
          onClick={() => {
            if (hasUpdateAccess || hasReadAccess) {
              navigate(`/workBoard/clients/${params?.id}`);
            }
          }}
        >
          {params?.value}
        </p>
      ),
    },

    {
      field: "clientName",
      headerName: "Name",
      width: 300,
      renderCell: (params) => (
        <div className="flex items-center">
          <Avatar sx={{ width: 32, height: 32, marginRight: 1 }} src={params?.row?.clientLogo} />
          <p
            onClick={() => {
              if (hasUpdateAccess || hasReadAccess) {
                navigate(`/workBoard/clients/${params?.id}`);
              }
            }}
            className={`table-font-size truncate max-w-64 overflow-hidden whitespace-nowrap cursor-pointer ${
              hasUpdateAccess || hasReadAccess ? "text-primary" : "text-gray-500"
            }`}
          >
            {params.value || "-"}
          </p>
        </div>
      ),
    },
    {
      field: "createdBy",
      headerName: "Created By",
      width: 160,
      renderCell: (params) => {
        if (params.row?.isSelfSignup) {
          return (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
              Self Signup
            </span>
          );
        }
        return renderUserCell(params.row?.createdBy);
      },
    },
    {
      field: "country",
      headerName: "Country",
      width: 170,
      renderCell: (params) => {
        return (
          <p className="table-font-size  cursor-pointer">{params?.row?.address?.country || "-"}</p>
        );
      },
    },
    {
      field: "projectsCount",
      width: 160,
      headerName: "Projects",
      renderCell: (params) => {
        return <p className="table-font-size  cursor-pointer">{params?.row?._count?.projects}</p>;
      },
    },
    {
      field: "workOrdersCount",
      width: 160,
      headerName: "Total Work Orders",
      renderCell: (params) => {
        return <p className="table-font-size  cursor-pointer">{params?.row?._count?.workOrders}</p>;
      },
    },
    {
      field: "publishedWorkOrdersCount",
      width: 160,
      headerName: "Published",
      renderCell: (params) => {
        return <p className="table-font-size  cursor-pointer">{params?.row?._count?.published}</p>;
      },
    },
    {
      field: "assignedWorkOrdersCount",
      width: 160,
      headerName: "Assigned",
      renderCell: (params) => {
        return <p className="table-font-size  cursor-pointer">{params?.row?._count?.assigned}</p>;
      },
    },
    {
      field: "workDoneWorkOrdersCount",
      width: 160,
      headerName: "Work Done",
      renderCell: (params) => {
        return <p className="table-font-size  cursor-pointer">{params?.row?._count?.workDone}</p>;
      },
    },
    {
      field: "approvedWorkOrdersCount",
      width: 160,
      headerName: "Approved",
      renderCell: (params) => {
        return <p className="table-font-size  cursor-pointer">{params?.row?._count?.approved}</p>;
      },
    },
    {
      field: "paidWorkOrdersCount",
      width: 160,
      headerName: "Paid",
      renderCell: (params) => {
        return <p className="table-font-size  cursor-pointer">{params?.row?._count?.paid}</p>;
      },
    },
    {
      field: "isVerified",
      headerName: "Status",
      width: 140,
      renderCell: (params) => {
        return params.row?.owner?.status === "APPROVED" ? (
          <Chip label="Active" color="primary" size="small" className="w-24 justify-center" />
        ) : params.row?.owner?.status === "DEACTIVATED" ? (
          <Chip label="In-Active" color="error" size="small" className="w-24 justify-center" />
        ) : params.row?.owner?.status === "PENDING" ? (
          <Chip
            label="Invited"
            color="primary"
            size="small"
            variant="outlined"
            className="w-24 justify-center"
          />
        ) : (
          "-"
        );
      },
    },

    (hasUpdateAccess || hasDeleteAccess) && {
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const userStatus = params?.row?.owner?.status;
        const filteredMenuOptions = menuOptions.filter((item) => {
          if (!userStatus) {
            if (item.value === "APPROVED" || item.value === "DEACTIVATED") {
              return false;
            }
          }
          if (userStatus === "PENDING") {
            if (item.value === "APPROVED" || item.value === "DEACTIVATED") {
              return false;
            }
          }
          if (userStatus === "APPROVED" && item.value === "APPROVED") {
            return false;
          }

          if (userStatus === "DEACTIVATED" && item.value === "DEACTIVATED") {
            return false;
          }
          if (userStatus === "PENDING" && (item.value === "INVITED" || item.value === "APPROVED")) {
            return false;
          }

          return true;
        });
        filteredMenuOptions.unshift({
          label: "View Profile",
          value: "VIEW_PROFILE",
        });
        if (!params.row?.owner) {
          filteredMenuOptions.push({
            label: "Generate Signup",
            value: "GENERATE_SIGNUP",
          });
        } else {
          filteredMenuOptions.push({
            label: "Delete Access",
            value: "DELETE_ACCOUNT",
          });
        }
        return (
          <BaseDropdownV2
            menuOptions={filteredMenuOptions}
            triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
            onItemClick={(value) => handleItemClick(value, params?.row)}
          />
        );
      },
      pinRight: true,
    },
  ].filter(Boolean);
  const { mutateAsync: deleteCustomerUserMutation } = useMutation({
    mutationFn: deleteCustomerUser,
  });

  const { mutateAsync: activateOwner, isPending: isActivatingOwner } = useMutation({
    mutationFn: activateCompanyUser,
    onSuccess: () => {
      dispatch(showToast({ message: "Customer activated successfully", type: "success" }));
      setIsConfirmActivateOpen(false);
      refetchClients();
    },
    onError: (error) => {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to activate customer",
          type: "error",
        }),
      );
    },
  });

  const { mutateAsync: deactivateOwner, isPending: isDeactivatingOwner } = useMutation({
    mutationFn: deactivateCompanyUser,
    onSuccess: () => {
      dispatch(showToast({ message: "Customer deactivated successfully", type: "success" }));
      setIsConfirmDeactivateOpen(false);
      refetchClients();
    },
    onError: (error) => {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to deactivate customer",
          type: "error",
        }),
      );
    },
  });

  const handleDeleteOwner = async () => {
    setIsDeletingOwner(true);
    try {
      await deleteCustomerUserMutation(selectedClient.id);
      setIsDeleteOwnerModalOpen(false);
      dispatch(showToast({ message: "Associated account deleted successfully", type: "success" }));
      refetchClients();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to delete account",
          type: "error",
        }),
      );
    } finally {
      setIsDeletingOwner(false);
    }
  };

  const handleItemClick = (value, params) => {
    if (value === "VIEW_PROFILE") {
      // Same place clicking the name goes: the parent customer's own page, opened
      // on its Profile tab, with its entities one tab away.
      navigate(`/workBoard/clients/${params?.id}?tab=profile`);
    }
    if (value === "EDIT") {
      navigate(`edit/${params?.id}?mode=DIRECT`);
    }
    if (value === "DELETE") {
      setSelectedClient({ id: params?.id, clientName: params?.clientName });
      setIsDeleteConfirmationModalOpen(true);
    }
    if (value === "GENERATE_SIGNUP") {
      setSelectedClient(params);
      setIsGenerateSignupOpen(true);
    }
    if (value === "DELETE_ACCOUNT") {
      setSelectedClient({ id: params?.id, clientName: params?.clientName });
      setIsDeleteOwnerModalOpen(true);
    }
    if (value === "APPROVED") {
      setSelectedClient({
        id: params?.id,
        clientName: params?.clientName,
        ownerId: params?.owner?.id,
      });
      setIsConfirmActivateOpen(true);
    }
    if (value === "DEACTIVATED") {
      setSelectedClient({
        id: params?.id,
        clientName: params?.clientName,
        ownerId: params?.owner?.id,
      });
      setIsConfirmDeactivateOpen(true);
    }
  };
  const handleDeleteClient = async (clientId) => {
    try {
      const payload = {
        id: clientId,
      };
      const response = await deleteClient(clientId);
      setOpenDropdownId(null);
      setIsDeleteConfirmationModalOpen(false);
      dispatch(
        showToast({
          message: "Customer deleted successfully",
          type: "success",
        }),
      );
      refetchClients();
    } catch (error) {
      dispatch(showToast({ message: error?.response?.data?.message, type: "error" }));
    }
  };

  const handlePaginationChange = (newPaginationModel) => {
    dispatch(
      setClientDashboardFilters({
        module: "workOrders",
        filterName: "pagination",
        data: {
          page: newPaginationModel.page,
          pageSize: newPaginationModel.pageSize,
        },
      }),
    );
  };

  return (
    <>
      {isFetchingClients && <BaseLoader isLoading={isFetchingClients} />}
      <BaseWrapper>
        <div className="bg-white rounded-lg px-5 py-3.5 shadow-md my-4 sticky top-10 z-10 flex justify-between items-center">
          <div className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary">
              <i className="fa-light fa-filter text-white text-sm"></i>
            </span>
            <p
              className="text-primary ml-2 cursor-pointer"
              onClick={() => setFiltersDrawerOpen(true)}
            >
              Add Filter
            </p>
          </div>
          <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
            <div className="flex gap-2">
              {appliedFilters.map((f) => (
                <div
                  key={f.key + f.value}
                  className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1"
                >
                  <span className="text-primary">{f.prefix}:</span>&nbsp;
                  <p className="text-primary text-sm mx-1">{f.value}</p>
                  <button
                    onClick={() => handleRemoveFilter(f.key, f.id)}
                    className="ml-2 text-primary hover:text-red-500"
                  >
                    <i className="fas fa-times text-xs"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="w-[28%]">
            <BaseTextField
              placeholder="Search by name, email or ID"
              value={searchInput}
              onChange={handleSearchChange}
              startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
            />
          </div>
        </div>

        <BaseDataTableV2
          className="max-h-[calc(100vh-300px)]"
          rowHeight={46}
          columns={columns}
          data={clientsList}
          isLoading={isFetchingClients}
          disableColumnResize={false}
          disableRowSelectionOnClick={true}
          checkboxSelection
          hideFooter={false}
          pagination
          paginationMode="server"
          paginationModel={clientDashboardFilters.pagination}
          onPaginationModelChange={handlePaginationChange}
          pageSizeOptions={[10, 20, 50, 100]}
          rowCount={rowCount}
        />
        {filtersDrawerOpen && (
          <ClientDashboardFilters
            open={filtersDrawerOpen}
            onClose={() => setFiltersDrawerOpen(false)}
            isLoading={isFetchingClients}
            setFiltersChanged={setFiltersChanged}
            filterType="client"
          />
        )}
        {isDeleteConfirmationModalOpen && (
          <BaseConfirmModal
            open={isDeleteConfirmationModalOpen}
            onClose={() => setIsDeleteConfirmationModalOpen(false)}
            cancelText="Cancel"
            confirmText="Delete"
            confirmButtonColor="error"
            title="Delete Customer"
            description={
              <p>
                Are you sure you want to delete the customer
                {
                  <span className="text-red-600 font-medium mx-1 capitalize">
                    "{selectedClient?.clientName}"
                  </span>
                }
                ? This action cannot be undone.
              </p>
            }
            onConfirm={() => handleDeleteClient(selectedClient?.id)}
          />
        )}
        {isProfileCompleted && (
          <CompleteProfileModal
            isProfileCompleted={isProfileCompleted}
            setIsProfileCompleted={setIsProfileCompleted}
            status={userData?.status}
            authRole={authRole}
          />
        )}
        {isGenerateSignupOpen && (
          <GenerateSignupModal
            open={isGenerateSignupOpen}
            close={() => setIsGenerateSignupOpen(false)}
            selectedClient={selectedClient}
            clientId={selectedClient?.id}
            onSuccess={refetchClients}
          />
        )}
        {isDeleteOwnerModalOpen && (
          <BaseConfirmModal
            open={isDeleteOwnerModalOpen}
            onClose={() => setIsDeleteOwnerModalOpen(false)}
            cancelText="Cancel"
            confirmText="Delete"
            confirmButtonColor="error"
            isLoading={isDeletingOwner}
            title="Delete Associated Account"
            description={
              <p>
                Are you sure you want to delete the account associated with{" "}
                <span className="text-red-600 font-medium mx-1 capitalize">
                  "{selectedClient?.clientName}"
                </span>
                ? This action cannot be undone.
              </p>
            }
            onConfirm={handleDeleteOwner}
          />
        )}
        {isConfirmActivateOpen && (
          <BaseConfirmModal
            open={isConfirmActivateOpen}
            onClose={() => setIsConfirmActivateOpen(false)}
            cancelText="Cancel"
            confirmText="Activate"
            isLoading={isActivatingOwner}
            title="Activate Customer"
            description={
              <p>
                Are you sure you want to activate
                <span className="text-primary font-medium mx-1 capitalize">
                  "{selectedClient?.clientName}"
                </span>
                ?
              </p>
            }
            onConfirm={() => activateOwner(selectedClient?.ownerId)}
          />
        )}
        {isConfirmDeactivateOpen && (
          <BaseConfirmModal
            open={isConfirmDeactivateOpen}
            onClose={() => setIsConfirmDeactivateOpen(false)}
            cancelText="Cancel"
            confirmText="Deactivate"
            confirmButtonColor="error"
            isLoading={isDeactivatingOwner}
            title="Deactivate Customer"
            description={
              <p>
                Are you sure you want to deactivate
                <span className="text-red-600 font-medium mx-1 capitalize">
                  "{selectedClient?.clientName}"
                </span>
                ? They will no longer be able to sign in.
              </p>
            }
            onConfirm={() => deactivateOwner(selectedClient?.ownerId)}
          />
        )}
      </BaseWrapper>
    </>
  );
};

export default ClientListingTable;
